import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AgencyActions } from './agency.actions';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Agency } from '@enrollhere/classes';
import { Action } from '@ngrx/store';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class AgencyEffects {
  private actions$ = inject(Actions);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private mapAgencyFromDoc(docSnapshot: {
    id: string;
    data: () => Record<string, unknown>;
  }) {
    return Agency.fromJSON({ ...docSnapshot.data(), id: docSnapshot.id });
  }

  private emitLoadAgenciesSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    }
  ) {
    const agencies = snapshot.docs.map(docSnapshot =>
      this.mapAgencyFromDoc(docSnapshot)
    );
    subscriber.next(AgencyActions.loadAgenciesSuccess({ agencies }));
  }

  private emitLoadAgenciesFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(AgencyActions.loadAgenciesFailure({ error }));
  }

  private emitLoadAgencyFromSnapshot(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      exists: () => boolean;
      id: string;
      data: () => Record<string, unknown>;
    }
  ) {
    if (!snapshot.exists()) {
      subscriber.next(
        AgencyActions.loadAgencyFailure({
          error: 'Agency not found',
        })
      );
      return;
    }

    const agency = Agency.fromJSON({
      ...snapshot.data(),
      id: snapshot.id,
    });
    subscriber.next(AgencyActions.loadAgencySuccess({ agency }));
  }

  private createAddAgencySuccess(agency: Agency, id: string) {
    return AgencyActions.addAgencySuccess({
      agency: Agency.fromJSON({ ...agency, id }),
    });
  }

  private createRemoveAgencySuccess(agencyId: string) {
    return AgencyActions.removeAgencySuccess({ agencyId });
  }

  private createUpdateAgencySuccess(agencyId: string, agency: Partial<Agency>) {
    return AgencyActions.updateAgencySuccess({
      agencyId,
      agency,
    });
  }

  loadAgencies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyActions.loadAgencies),
      switchMap(() => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(this.firestore, 'agencies'),
            snapshot => {
              this.emitLoadAgenciesSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                }
              );
            },
            error => {
              this.emitLoadAgenciesFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Agency API] Load Agencies Error', error })
          )
        );
      })
    );
  });

  loadAgency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyActions.loadAgency),
      switchMap(({ agencyId }) =>
        new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            doc(this.firestore, 'agencies', agencyId),
            snapshot => {
              this.emitLoadAgencyFromSnapshot(
                subscriber,
                snapshot as {
                  exists: () => boolean;
                  id: string;
                  data: () => Record<string, unknown>;
                }
              );
            },
            error => {
              subscriber.next(AgencyActions.loadAgencyFailure({ error }));
            }
          );
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Agency API] Load Agency Error', error })
          )
        )
      )
    );
  });

  addAgency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyActions.addAgency),
      mergeMap(async ({ agency }) => {
        try {
          const docRef = await addDoc(
            collection(this.firestore, 'agencies'),
            agency
          );
          return this.createAddAgencySuccess(agency, docRef.id); // return new agency with id
        } catch (error) {
          return AgencyActions.addAgencyFailure({ error });
        }
      })
    );
  });

  removeAgency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyActions.removeAgency),
      mergeMap(async ({ agencyId }) => {
        try {
          await deleteDoc(doc(this.firestore, 'agencies', agencyId));
          return this.createRemoveAgencySuccess(agencyId); // return removed agency's id
        } catch (error) {
          return AgencyActions.removeAgencyFailure({ error });
        }
      })
    );
  });

  updateAgency$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyActions.updateAgency),
      mergeMap(async ({ agencyId, agency }) => {
        try {
          await setDoc(doc(this.firestore, 'agencies', agencyId), agency, {
            merge: true,
          });
          return this.createUpdateAgencySuccess(agencyId, agency); // return updated agency's id and changes
        } catch (error) {
          return AgencyActions.updateAgencyFailure({ error });
        }
      })
    );
  });
}
